import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import {
    LibraryFolderType,
    LibraryMediaGroup,
    LibraryRequestPayload,
    MediaFolder,
    MediaResource,
    MediaSearchResponse,
    PreviewQueryConfig,
    SchoolLibraryResponse,
    SearchGroupsNormalized,
    SearchItemNormalized,
    SearchQueryConfig,
    SearchResultsNormalized
} from '../../models/library';
import { countFolderResources, MAX_LIBRARY_FOLDER_DEPTH } from './library-folder-utils';

@Injectable({
    providedIn: 'root'
})
export class LibraryService {
    private readonly http = inject(HttpClient);

    public queryMediaHub(config: SearchQueryConfig): Observable<SearchResultsNormalized> {
        let params = new HttpParams()
            .set('schoolCode', config.schoolCode ?? '')
            .set('campusCode', config.campusCode ?? '')
            .set('searchTerm', config.searchString ?? '')
            .set('offset', String(config.offset ?? 0))
            .set('limit', String(config.limit ?? 0));

        for (const type of config.mediaTypes ?? []) {
            params = params.append('mediaType', type);
        }

        const endpoint = `${environment.apiBaseUrl}/umbraco/api/media/search`;
        return this.http.get<MediaSearchResponse>(endpoint, { params }).pipe(
            map(response => this.transformGatewayPayload(response))
        );
    }

    private transformGatewayPayload(payload: MediaSearchResponse | null | undefined): SearchResultsNormalized {
        const groups: SearchGroupsNormalized = {
            documents: [],
            videos: [],
            gallery: []
        };

        if (!payload?.Containers) {
            return {
                groups,
                paging: {
                    TotalResults: payload?.Paging?.TotalResults ?? 0,
                    Offset: payload?.Paging?.Offset ?? 0,
                    Limit: payload?.Paging?.Limit ?? 0,
                    HasMore: payload?.Paging?.HasMore ?? false
                }
            };
        }

        for (const container of payload.Containers) {
            const groupKey = this.resolveGroupKey(container.FolderType);
            if (!groupKey) continue;

            const target = groups[groupKey];

            if (container.Folders?.length) {
                this.flattenFolders(container.Folders, target, 0);
            }

            container.Resources?.forEach(resource => target.push(this.mapResourceToCard(resource)));
        }

        const normalizedCount = groups.documents.length + groups.videos.length + groups.gallery.length;

        return {
            groups,
            paging: {
                TotalResults: payload.Paging?.TotalResults ?? normalizedCount,
                Offset: payload.Paging?.Offset ?? 0,
                Limit: payload.Paging?.Limit ?? normalizedCount,
                HasMore: payload.Paging?.HasMore ?? false
            }
        };
    }

    private resolveGroupKey(folderType: LibraryFolderType): LibraryMediaGroup | null {
        const groupByFolderType: Record<LibraryFolderType, LibraryMediaGroup> = {
            [LibraryFolderType.Photos]: LibraryMediaGroup.Gallery,
            [LibraryFolderType.Documents]: LibraryMediaGroup.Documents,
            [LibraryFolderType.Videos]: LibraryMediaGroup.Videos,
        };
        return groupByFolderType[folderType] ?? null;
    }

    private flattenFolders(folders: MediaFolder[], target: SearchItemNormalized[], depth: number): void {
        if (depth >= MAX_LIBRARY_FOLDER_DEPTH) return;

        for (const folder of folders) {
            target.push({
                Id: folder.Id,
                Name: folder.Name,
                IsFolder: true,
                ResourceCount: countFolderResources(folder, depth, MAX_LIBRARY_FOLDER_DEPTH),
                CoverUrl: folder.PreviewUrls?.[0] ?? null,
                RawPayload: folder
            });

            if (folder.SubFolders?.length) {
                this.flattenFolders(folder.SubFolders, target, depth + 1);
            }

            folder.Resources?.forEach(resource => target.push(this.mapResourceToCard(resource)));
        }
    }

    private mapResourceToCard(res: MediaResource): SearchItemNormalized {
        return {
            Id: res.Id,
            Name: res.Name,
            IsFolder: false,
            ResourceCount: 0,
            FileExtension: res.FileExtension,
            CoverUrl: res.ThumbnailUrl ?? res.ImageUrl ?? null,
            RawPayload: res
        };
    }

    getSchoolLibraryData(payload: LibraryRequestPayload): Observable<SchoolLibraryResponse> {
        const endpoint = `${environment.apiBaseUrl}/umbraco/api/libraryAPI/School`;
        return this.http.post<SchoolLibraryResponse>(endpoint, payload);
    }

    getPreviewContent(config: PreviewQueryConfig): Observable<MediaSearchResponse> {
        let params = new HttpParams()
            .set('schoolCode', config.schoolCode ?? '')
            .set('offset', String(config.offset ?? 0))
            .set('limit', String(config.limit ?? 10));

        if (config.campusCode) {
            params = params.set('campusCode', config.campusCode);
        }

        const endpoint = `${environment.apiBaseUrl}/umbraco/api/libraryPreviewContentAPI/search`;
        return this.http.get<MediaSearchResponse>(endpoint, {
            params,
            // headers: { 'X-Preview-Token': config.token }
        });
    }
}
